'use client';

import type { FC } from 'react';

import {
  BLOG_ARTICLES_PER_PAGE,
  BLOG_RECENT_ARTICLES_LIMIT,
} from '@core/Constants/BlogHubPagination';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import { Grid } from '@gds/Grid/Grid';
import { Pagination } from '@gds/Pagination/Pagination';
import { PaginationItem } from '@gds/Pagination/PaginationItem';
import { Typography } from '@gds/Typography/Typography';
import type { Responses } from '@kontent-ai/delivery-sdk';

import { ArticleCard } from '../ArticleCard/ArticleCard';
import { blogToCardConverter } from '../ArticleCard/Utils/BlogToCard';
import { getUrlForPagination } from './Utils/GetUrlForPagination';

import styles from './AllArticles.module.css';

type Props = {
  title: string;
  allArticles: Responses.IListContentItemsResponse<any>;
  linkPrefix: string;
  locale: Locale;
  paginationUrl: string;
};

const AllArticles: FC<Props> = ({ allArticles, title, linkPrefix, locale, paginationUrl }) => {
  const { totalCount, skip } = allArticles.pagination;
  const currentPage = Math.ceil((skip - BLOG_RECENT_ARTICLES_LIMIT) / BLOG_ARTICLES_PER_PAGE) + 1;
  const totalPages = totalCount
    ? Math.ceil((totalCount - BLOG_RECENT_ARTICLES_LIMIT) / BLOG_ARTICLES_PER_PAGE)
    : 1;

  const getUrlFromPageNumber = getUrlForPagination(paginationUrl);

  return (
    <section className={styles.allArticles}>
      <Typography variant="h2" className={styles.title}>
        {title}
      </Typography>
      <Grid.Row className={styles.gridRow} justify="left">
        {allArticles.items.map(blogToCardConverter).map(article => (
          <Grid.Col
            key={article.slug}
            width={{ mobile: 12, tablet: 6, desktop: 4 }}
            className={styles.article}
          >
            <ArticleCard article={article} linkPrefix={linkPrefix} locale={locale} />
          </Grid.Col>
        ))}
      </Grid.Row>
      {totalPages > 1 ? (
        <Grid.Row justify="center">
          <div className={styles.pagination}>
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              renderItem={paginationItem => (
                <PaginationItem
                  component="Link"
                  href={getUrlFromPageNumber(paginationItem.page)}
                  {...paginationItem}
                />
              )}
            />
          </div>
        </Grid.Row>
      ) : null}
    </section>
  );
};
export { AllArticles };
